import type { TransportMapLine } from '../types'

export interface FloatingLabelLayoutItem {
  id: string
  x: number
  y: number
  width: number
  height: number
}

export interface FloatingLabelOffset {
  x: number
  y: number
}

interface Rectangle {
  left: number
  right: number
  top: number
  bottom: number
}

function segmentLength(
  first: { latitude: number; longitude: number },
  second: { latitude: number; longitude: number },
) {
  const latitude = (first.latitude + second.latitude) / 2
  const longitudeScale = Math.cos(latitude * Math.PI / 180)
  const latitudeDelta = second.latitude - first.latitude
  const longitudeDelta = (second.longitude - first.longitude) * longitudeScale

  return Math.hypot(latitudeDelta, longitudeDelta)
}

function pathLength(path: Array<{ latitude: number; longitude: number }>) {
  let total = 0

  for (let index = 1; index < path.length; index += 1) {
    total += segmentLength(path[index - 1], path[index])
  }

  return total
}

function midpointAlongPath(path: Array<{ latitude: number; longitude: number }>) {
  if (!path.length) return null
  if (path.length === 1) return path[0]

  const total = pathLength(path)
  if (total === 0) return path[Math.floor(path.length / 2)]

  const target = total / 2
  let travelled = 0

  for (let index = 1; index < path.length; index += 1) {
    const first = path[index - 1]
    const second = path[index]
    const segment = segmentLength(first, second)

    if (travelled + segment >= target) {
      const ratio = segment === 0 ? 0 : (target - travelled) / segment

      return {
        latitude: first.latitude + (second.latitude - first.latitude) * ratio,
        longitude: first.longitude + (second.longitude - first.longitude) * ratio,
      }
    }

    travelled += segment
  }

  return path[path.length - 1]
}

export function lineLabelAnchor(line: TransportMapLine) {
  const geometryPaths = line.directions
    .map((direction) => direction.geometry)
    .filter((path) => path.length > 0)
    .sort((first, second) => pathLength(second) - pathLength(first))

  if (geometryPaths.length) return midpointAlongPath(geometryPaths[0])

  const stopCoordinates = line.directions
    .flatMap((direction) => direction.stops)
    .filter((stop) => stop.latitude != null && stop.longitude != null)
    .map((stop) => ({
      latitude: Number(stop.latitude),
      longitude: Number(stop.longitude),
    }))

  return midpointAlongPath(stopCoordinates)
}

function intersects(first: Rectangle, second: Rectangle, gap = 8) {
  return !(
    first.right + gap <= second.left
    || first.left >= second.right + gap
    || first.bottom + gap <= second.top
    || first.top >= second.bottom + gap
  )
}

function rectangleFor(
  item: FloatingLabelLayoutItem,
  offset: FloatingLabelOffset,
): Rectangle {
  const centerX = item.x + offset.x
  const centerY = item.y + offset.y

  return {
    left: centerX - item.width / 2,
    right: centerX + item.width / 2,
    top: centerY - item.height / 2,
    bottom: centerY + item.height / 2,
  }
}

function candidates(item: FloatingLabelLayoutItem): FloatingLabelOffset[] {
  const horizontal = Math.max(92, item.width * .72)
  const vertical = Math.max(42, item.height + 12)

  return [
    { x: 0, y: -30 },
    { x: 0, y: -30 - vertical },
    { x: horizontal, y: -30 },
    { x: -horizontal, y: -30 },
    { x: horizontal, y: -30 - vertical },
    { x: -horizontal, y: -30 - vertical },
    { x: 0, y: 18 },
    { x: horizontal, y: 18 },
    { x: -horizontal, y: 18 },
    { x: horizontal * 2, y: -30 },
    { x: -horizontal * 2, y: -30 },
  ]
}

export function layoutFloatingLabels(items: FloatingLabelLayoutItem[]) {
  const offsets = new Map<string, FloatingLabelOffset>()
  const placed: Rectangle[] = []

  for (const item of items) {
    let chosen: FloatingLabelOffset | null = null

    for (const candidate of candidates(item)) {
      const rectangle = rectangleFor(item, candidate)

      if (placed.every((existing) => !intersects(rectangle, existing))) {
        chosen = candidate
        placed.push(rectangle)
        break
      }
    }

    if (!chosen) {
      chosen = {
        x: 0,
        y: -30 - placed.length * (item.height + 10),
      }
      placed.push(rectangleFor(item, chosen))
    }

    offsets.set(item.id, chosen)
  }

  return offsets
}
